import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

export function AdminFinancialTab({ financialData }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-green-600">R$ {financialData.gross_revenue?.toFixed(2)}</p><p className="text-xs text-slate-500 mt-1">Faturamento Bruto/Mes</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-red-600">R$ {financialData.total_costs?.toFixed(2)}</p><p className="text-xs text-slate-500 mt-1">Custos Totais</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-blue-600">R$ {financialData.net_revenue?.toFixed(2)}</p><p className="text-xs text-slate-500 mt-1">Receita Liquida</p></CardContent></Card>
        <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center"><p className="text-3xl font-bold text-slate-700">{financialData.total_subscribers}</p><p className="text-xs text-slate-500 mt-1">Total Assinantes</p></CardContent></Card>
      </div>

      <Card className="bg-white border border-slate-200">
        <CardHeader><CardTitle className="text-lg text-slate-900">Faturamento por Plano</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-xs text-slate-500"><th className="text-left py-2">Plano</th><th className="text-center">Assinantes</th><th className="text-right">Faturamento</th></tr></thead>
            <tbody>
              {Object.entries(financialData.plan_counts || {}).map(([plan, count]) => (
                <tr key={plan} className="border-b border-slate-50">
                  <td className="py-2 capitalize font-medium">{plan}</td>
                  <td className="text-center">{count}</td>
                  <td className="text-right font-bold text-green-600">R$ {(financialData.plan_revenue?.[plan] || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200">
        <CardHeader><CardTitle className="text-lg text-slate-900">Detalhamento de Custos</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-xs text-slate-500"><th className="text-left py-2">Categoria</th><th className="text-center">Taxa</th><th className="text-right">Valor</th></tr></thead>
            <tbody>
              {financialData.fees && Object.entries(financialData.fees).map(([key, fee]) => (
                <tr key={key} className="border-b border-slate-50">
                  <td className="py-2 capitalize">{key.replace(/_/g, ' ')}</td>
                  <td className="text-center">{fee.rate ? `${(fee.rate * 100).toFixed(0)}%` : '-'}</td>
                  <td className="text-right font-bold text-red-600">R$ {(fee.value || 0).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 font-bold">
                <td className="py-2" colSpan="2">Total de Custos</td>
                <td className="text-right text-red-700">R$ {financialData.total_costs?.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200" data-testid="financial-monthly-chart">
          <CardHeader><CardTitle className="text-lg text-slate-900">Entradas Mensais</CardTitle></CardHeader>
          <CardContent>
            {financialData.monthly_evolution?.length > 0 && (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={financialData.monthly_evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                  <Bar dataKey="receita" fill="#22c55e" radius={[4, 4, 0, 0]} name="Receita (R$)" />
                  <Bar dataKey="novos" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Novos Lojistas" />
                  <Bar dataKey="cancelados" fill="#ef4444" radius={[4, 4, 0, 0]} name="Cancelados" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200" data-testid="financial-projection-chart">
          <CardHeader><CardTitle className="text-lg text-slate-900">Projecao Anual</CardTitle></CardHeader>
          <CardContent>
            {financialData.annual_projection?.length > 0 && (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={financialData.annual_projection}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }} />
                  <Area type="monotone" dataKey="projecao" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} strokeWidth={2} name="Projecao (R$)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {financialData.influencer_payments?.length > 0 && (
        <Card className="bg-white border border-slate-200" data-testid="influencer-payments-table">
          <CardHeader><CardTitle className="text-lg text-slate-900">Pagamentos por Influenciador</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200 text-xs text-slate-500"><th className="text-left py-2">Influenciador</th><th className="text-center">Lojistas</th><th className="text-center">PIX</th><th className="text-right">Total</th><th className="text-right">Pendente</th><th className="text-right">Pago</th></tr></thead>
                <tbody>
                  {financialData.influencer_payments.map(ip => (
                    <tr key={ip.user_id} className="border-b border-slate-50">
                      <td className="py-2 font-medium">{ip.name}</td>
                      <td className="text-center">{ip.active_merchants}</td>
                      <td className="text-center text-xs">{ip.pix || '-'}</td>
                      <td className="text-right font-bold">R$ {ip.total_commission.toFixed(2)}</td>
                      <td className="text-right text-amber-600">R$ {ip.pending_commission.toFixed(2)}</td>
                      <td className="text-right text-green-600">R$ {ip.paid_commission.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
